import { BaseNodeModel } from '../../../models';
import {
  Design,
  DesignModule,
  DesignModuleNode,
  DesignModuleNodeConfigReferenceType,
  DesignObject,
  NodeType
} from '../../../types';
import { normalizeDisambiguationPrompt } from './normalize-disambiguation-prompt';
import { normalizeNoInputTimeoutOverride } from './normalize-no-input-timeout-override';
import { normalizePrompt } from './normalize-prompt';

type Normalizable = DesignObject | DesignModuleNode | BaseNodeModel;

export type Normalizer = <
  T extends Normalizable
>(
  item: T,
  serialized: Design,
  /** If @see item is a node, then this is the module it is in. */
  appModule?: DesignModule
) => T;

/** Normalize a @see DesignObject or @see DesignModuleNode. */
export const normalizeObjectOrNode = <T extends Normalizable>(
  item: T,
  serialized: Design = window.apm.serialized,
  appModule?: DesignModule
): T => {
  switch ('_class' in item ? item._class : item.constructor.name as NodeType) {
    case NodeType.input:
    case NodeType.richText:
      return normalizeInputOrRichText(item, serialized);
    case NodeType.prompt:
      return normalizePrompt(item, serialized, appModule);
    case NodeType.promptReference:
      return normalizePromptReference(item, serialized);
  }

  return item;
};

const normalizeInputOrRichText: Normalizer = (item, serialized) => {
  const { config } = item.props;
  const { noInputTimeout } = config;

  normalizeDisambiguationPrompt(config, serialized);
  normalizeNoInputTimeoutOverride(config, noInputTimeout);

  return item;
};

const variableSearch = /^\s*((app|mod)\.\S+)\s*$/;
const normalizePromptReference: Normalizer = (item) => {
  const { config } = item.props;
  if (!config.referenceType) {
    // Handle cases where reference type is unset.
    if (
      !config.variableReference
      && variableSearch.test(config.promptReference!)
    ) {
      // Handle cases where variables were in `promptReference` instead of `variableReference`.
      config.variableReference = config.promptReference;
      delete config.promptReference;
    }
    config.referenceType ??= (config.variableReference) ? (
      DesignModuleNodeConfigReferenceType.variable
    ) : DesignModuleNodeConfigReferenceType.prompt;
  }

  if (
    config.referenceType !== DesignModuleNodeConfigReferenceType.prompt
    && config.promptReference
  ) {
    delete config.promptReference;
  }

  if (
    config.referenceType !== DesignModuleNodeConfigReferenceType.variable
    && config.variableReference
  ) {
    delete config.variableReference;
  }

  return item;
};
